import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './Clients.css';
import { useClientsArchive, useClientsFilterOptions } from '../../hooks/useClients';
import { clientsService } from '../../services/clientsService';

export default function ClientsArchive() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    client: '',
    clientSearch: '',
    curator: '',
    fieldOfActivity: '',
    withoutTasks: false,
    year: '',
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(100);

  const clientsQuery = useClientsArchive({
    client: appliedFilters.client || undefined,
    client_search:
      !appliedFilters.client && appliedFilters.clientSearch?.trim()
        ? appliedFilters.clientSearch.trim()
        : undefined,
    curator: appliedFilters.curator || undefined,
    field_of_activity: appliedFilters.fieldOfActivity || undefined,
    without_tasks: appliedFilters.withoutTasks || undefined,
    year: appliedFilters.year || undefined,
    page: currentPage,
    per_page: recordsPerPage,
  });

  const filterOptionsQuery = useClientsFilterOptions(true, { archived: true });

  const clients = clientsQuery.data?.data || [];
  const totalClients = clientsQuery.data?.total || 0;
  const filterOptions = filterOptionsQuery.data || {
    client_names: [],
    curators: [],
    fields_of_activity: [],
    years: [],
  };

  const clientOptionsForSelect = useMemo(() => {
    const full = (filterOptions.client_names ?? []).filter(Boolean);
    const q = (filters.clientSearch || '').trim().toLowerCase();
    const narrowed = q ? full.filter((n: string) => n.toLowerCase().includes(q)) : full;
    if (filters.client && !narrowed.includes(filters.client)) return [filters.client, ...narrowed];
    return narrowed;
  }, [filterOptions.client_names, filters.clientSearch, filters.client]);

  const handleRecordsPerPageChange = (value: number) => {
    setRecordsPerPage(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (field: string, value: string | boolean) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleShowResults = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const handleExportExcel = () => {
    // Экспорт оставляем общий (по тем же фильтрам), бэкенд сейчас экспортирует общий список.
    clientsService
      .downloadClientsExcel({
        client: appliedFilters.client || undefined,
        curator: appliedFilters.curator || undefined,
        field_of_activity: appliedFilters.fieldOfActivity || undefined,
        without_tasks: appliedFilters.withoutTasks || undefined,
        year: appliedFilters.year || undefined,
        archived: true,
      })
      .catch((e: any) => {
        alert(e?.message || 'Ошибка при экспорте в Excel');
      });
  };

  return (
    <div className="clients-page">
      <div className="clients-header">
        <h1 className="clients-title">
          <img src="/icons/clients/clients.svg" alt="Клиенты" className="clients-title-icon" />
          Архив клиентов
        </h1>
      </div>

      <div className="clients-content">
        <div className="clients-sidebar">
          <div className="clients-statistics">
            <h3 className="clients-statistics-title">Клиентов в архиве</h3>
            <div className="clients-statistics-value-wrapper">
              <img src="/icons/clients/clients.svg" alt="Клиентов в архиве" className="clients-statistics-icon" />
              <div className="clients-statistics-value">{totalClients.toLocaleString('ru-RU')}</div>
            </div>
          </div>

          <div className="clients-filters-wrapper">
            <h3 className="clients-filters-title">Фильтрация</h3>
            <div className="clients-filters">
              <div className="clients-filter-group">
                <label className="clients-filter-label">Клиент</label>
                <div className="clients-client-search-wrapper">
                  <svg className="clients-client-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 14L11.1 11.1" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    type="text"
                    className="clients-client-search-input"
                    placeholder="Введите название клиента."
                    value={filters.clientSearch}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleFilterChange('clientSearch', v);
                      setAppliedFilters((prev) => ({
                        ...prev,
                        clientSearch: v,
                        client: prev.client ? prev.client : '',
                      }));
                      setCurrentPage(1);
                    }}
                    aria-label="Введите название клиента."
                  />
                </div>
                <div className="clients-filter-select-wrapper">
                  <select
                    className="clients-filter-select"
                    value={filters.client}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleFilterChange('client', v);
                      if (v) setFilters((prev) => ({ ...prev, clientSearch: '' }));
                    }}
                  >
                    <option value="">Выберите из списка</option>
                    {clientOptionsForSelect.map((name: string) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="clients-filter-group">
                <label className="clients-filter-label">Куратор</label>
                <div className="clients-filter-select-wrapper">
                  <select
                    className="clients-filter-select"
                    value={filters.curator}
                    onChange={(e) => handleFilterChange('curator', e.target.value)}
                  >
                    <option value="">Выберите из списка</option>
                    {filterOptions.curators.map((curator: string) => (
                      <option key={curator} value={curator}>
                        {curator}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="clients-filter-group">
                <label className="clients-filter-label">Сфера деятельности</label>
                <div className="clients-filter-select-wrapper">
                  <select
                    className="clients-filter-select"
                    value={filters.fieldOfActivity}
                    onChange={(e) => handleFilterChange('fieldOfActivity', e.target.value)}
                  >
                    <option value="">Выберите из списка</option>
                    {filterOptions.fields_of_activity.map((field: string) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="clients-filter-group">
                <label className="clients-filter-label">Кол-во задач</label>
                <div className="clients-filter-checkbox">
                  <input
                    type="checkbox"
                    id="withoutTasksArchive"
                    checked={filters.withoutTasks}
                    onChange={(e) => handleFilterChange('withoutTasks', e.target.checked)}
                  />
                  <label htmlFor="withoutTasksArchive">Без задач</label>
                </div>
              </div>

              <div className="clients-filter-group">
                <label className="clients-filter-label">Год начала сотрудничества</label>
                <div className="clients-filter-select-wrapper">
                  <select
                    className="clients-filter-select"
                    value={filters.year}
                    onChange={(e) => handleFilterChange('year', e.target.value)}
                  >
                    <option value="">Выберите год</option>
                    {filterOptions.years.map((year: number) => (
                      <option key={year} value={year.toString()}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button className="clients-filter-btn" onClick={handleShowResults}>
                Показать результаты
              </button>

              <button className="clients-export-btn clients-export-btn--sidebar" onClick={() => navigate('/clients')}>
                <img src="/icons/projects/archive.svg" alt="Назад" className="clients-export-icon" />
                Активные клиенты
              </button>

              <button className="clients-export-btn clients-export-btn--sidebar" onClick={handleExportExcel}>
                <img src="/icons/clients/DownloadSimple.svg" alt="Экспорт" className="clients-export-icon" />
                Экспортировать в Excel
              </button>
            </div>
          </div>
        </div>

        <div className="clients-main">
          <div className="clients-table-wrapper">
            <table className="clients-table">
              <thead>
                <tr>
                  <th>Клиент</th>
                  <th>Куратор</th>
                  <th>Сфера деятельности</th>
                  <th>Активные проекты / дела / задачи</th>
                </tr>
              </thead>
              <tbody>
                {clientsQuery.isLoading || clientsQuery.isFetching ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '40px' }}>
                      Загрузка...
                    </td>
                  </tr>
                ) : clients.length > 0 ? (
                  clients.map((client) => (
                    <tr
                      key={client.id}
                      className="clients-table-row"
                      onClick={() => navigate(`/clients/${client.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{client.name}</td>
                      <td>{client.curator || '—'}</td>
                      <td>{client.field_of_activity || '—'}</td>
                      <td>{(client.projects ?? 0)}/{(client.cases ?? 0)}/{(client.tasks ?? 0)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '40px' }}>
                      Нет данных для отображения
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="clients-pagination">
            <div className="clients-pagination-left">
              <div className="clients-pagination-select-wrapper">
                <select
                  className="clients-pagination-select"
                  value={recordsPerPage}
                  onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <span className="clients-pagination-text">Записей на странице</span>
            </div>
            <div className="clients-pagination-right">
              <button
                className="clients-pagination-arrow"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              <span className="clients-pagination-page">
                {currentPage}
              </span>
              <button
                className="clients-pagination-arrow"
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={clients.length < recordsPerPage}
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

